import React, {Component} from 'react';
import {connect} from 'react-redux';
import {
  Button, IconButton, Tooltip, Snackbar, Typography,
} from '@material-ui/core';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import {
  AccountTree as AccountTreeIcon,
  CompareArrows as CompareArrowsIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Save as SaveIcon,
  Publish as PublishIcon,
  Print as PrintIcon,
  Warning as WarningIcon,
  TableChart as TableChartIcon,
  AccountTree as GraphIcon,
  GetApp as ImportIcon,
} from '@material-ui/icons';
import * as A from '../store/lineageActions';
import {layoutGraph} from '../util/autoLayout';
import {rfRef} from './rfInstance';
import LineageCanvas from './LineageCanvas';
import NodeInspector from './NodeInspector';
import LineageTableView from './table/LineageTableView';
import ImportModal from './ImportModal';

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'});
};

const SaveStatus = ({saveState, lastSavedAt}) => {
  if (saveState === 'saving') return <span className="ln-save-status ln-save-saving" data-testid="save-status">Saving…</span>;
  if (saveState === 'saved') return <span className="ln-save-status ln-save-saved" data-testid="save-status">Saved {fmtTime(lastSavedAt)}</span>;
  if (saveState === 'error') return <span className="ln-save-status ln-save-error" data-testid="save-status">Save failed</span>;
  return null;
};

class LineageEditorShell extends Component {
  constructor(props) {
    super(props);
    this._onKey = this._onKey.bind(this);
    this.state = {published: false, showImportModal: false, importMenuAnchor: null};
  }

  componentDidMount() {
    this.props.loadCharacteristics();
    this.props.loadNodeRefs();
    this.props.loadGlobalSettings();
    window.addEventListener('keydown', this._onKey);
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this._onKey);
  }

  _onKey(e) {
    // Guard: don't intercept undo/redo/save when typing in a form field (Fable G3)
    if (/^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName)) return;
    if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
      e.preventDefault();
      this.props.undo();
    } else if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || (e.shiftKey && e.key === 'z'))) {
      e.preventDefault();
      this.props.redo();
    } else if ((e.metaKey || e.ctrlKey) && e.key === 's') {
      e.preventDefault();
      this.props.saveMap();
    }
  }

  _doTidy() {
    const {map} = this.props;
    const positions = layoutGraph(map.nodes, map.edges);
    if (!positions.length) return;
    this.props.applyNodeChanges(positions.map((p) => ({id: p.id, type: 'position', position: p.position, dragging: false})));
    this.props.commitHistory();
    if (rfRef.current) {
      requestAnimationFrame(() => rfRef.current && rfRef.current.fitView({padding: 0.2, duration: 300}));
    }
  }

  _doTidyDataView() {
    this.props.clearDataViewLayout();
    if (rfRef.current) requestAnimationFrame(() => rfRef.current && rfRef.current.fitView({padding: 0.2, duration: 300}));
  }

  _doPrint() {
    if (rfRef.current) {
      rfRef.current.fitView({padding: 0.08, duration: 0});
    }
    requestAnimationFrame(() => window.print());
  }

  async _doPublish() {
    const result = await this.props.publishMap();
    if (result && result.ok) {
      this.setState({published: true});
    }
  }

  _downloadJson() {
    const {map} = this.props;
    const blob = new Blob([JSON.stringify(map, null, 2)], {type: 'application/json'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lineage-${map.id}-conflict-copy.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  render() {
    const {
      map, viewMode, lineageViewType, highlightMode, drawerOpen,
      historyIndex, historyLength, onBack,
      saveState, lastSavedAt, saveError, displayMode, charLoadError,
    } = this.props;
    const {published} = this.state;

    const isConflict = saveState === 'error' && saveError === 'conflict';
    const isForbidden = saveState === 'error' && saveError === 'forbidden';
    const isPublished = map.status === 'published';
    const canUndo = historyIndex > 0;
    const canRedo = historyIndex < historyLength - 1;

    const selectedAppIds = map.nodes.filter((n) => n.selected && n.kind === 'app').map((n) => n.id);
    const selectedGroup = map.nodes.find((n) => n.selected && n.kind === 'group');

    const doGroup = () => this.props.groupSelected(selectedAppIds);
    const doUngroup = () => {
      if (selectedGroup) this.props.ungroup(selectedGroup.id);
    };

    return (
      <div
        className={`ln-editor${drawerOpen ? ' ln-drawer-open ln-panel-open' : ''}`}
        data-testid="lineage-editor"
      >
        {/* Slice 6c-3b — the characteristic palette came back EMPTY because the load was refused,
            not because none are configured. Without this the two are indistinguishable on screen,
            which is how enforcing /lineage would have silently degraded the page for a custodian or
            champion. Reuses the conflict-banner styling rather than inventing a surface. */}
        {charLoadError && (
          <div className="ln-conflict-banner ln-conflict-forbidden" data-testid="char-load-error-banner">
            <WarningIcon style={{fontSize: 16, marginRight: 6}} />
            {charLoadError}
          </div>
        )}
        {/* Conflict banner */}
        {isConflict && (
          <div className="ln-conflict-banner" data-testid="conflict-banner">
            <WarningIcon style={{fontSize: 16, marginRight: 6}} />
            Another user saved while you were editing.
            <Button size="small" onClick={() => this.props.loadMap(map.id)} data-testid="conflict-reload">Reload</Button>
            <Button size="small" onClick={() => this._downloadJson()} data-testid="conflict-download">Download JSON</Button>
          </div>
        )}
        {isForbidden && (
          <div className="ln-conflict-banner ln-conflict-forbidden" data-testid="forbidden-banner">
            You do not have permission to edit this diagram.
          </div>
        )}
        {(published || isPublished) && viewMode === 'view' && (
          <div className="ln-published-banner" data-testid="published-banner">
            This diagram is published (read-only).
            <Button size="small" onClick={() => this.props.setViewMode('edit')} data-testid="published-edit-btn">Edit draft</Button>
          </div>
        )}

        {/* Toast */}
        <Snackbar
          open={!!this.props.notification}
          autoHideDuration={6000}
          onClose={() => this.props.dismissNotify()}
          message={this.props.notification ? this.props.notification.message : ''}
          anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
          data-testid="lineage-toast"
          action={this.props.notification && this.props.notification.actionLabel ? (
            <Button
              size="small"
              color="secondary"
              data-testid="lineage-toast-action"
              onClick={() => {
                if (this.props.notification.actionFn) this.props.notification.actionFn();
                this.props.dismissNotify();
              }}
            >
              {this.props.notification.actionLabel}
            </Button>
          ) : null}
        />

        {/* Toolbar — plain absolutely-positioned div (was an RF Panel) */}
        <div className="ln-toolbar">
          <div className="ln-toolbar-row ln-toolbar-row-main">
            <a href="#back" className="ln-back" onClick={(e) => {
              e.preventDefault();
              if (onBack) onBack();
            }}>
              <i className="material-icons">arrow_back</i> Back
            </a>
            {viewMode === 'edit' ? (
              <input
                className="ln-title-input"
                value={map.name || ''}
                placeholder="Untitled Lineage Map"
                title="Name this lineage"
                data-testid="lineage-name-input"
                onChange={(e) => this.props.setMapProperty('name', e.target.value)}
              />
            ) : (
              <Typography variant="subtitle1" className="ln-title">{map.name || 'Untitled Lineage Map'}</Typography>
            )}
          </div>

          <div className="ln-toolbar-row ln-toolbar-row-actions">
            <ToggleButtonGroup size="small" exclusive value={lineageViewType} onChange={(e, v) => v && this.props.setLineageViewType(v)}>
              <Tooltip title="System lineage"><ToggleButton value="system" data-testid="toggle-system"><AccountTreeIcon fontSize="small" /></ToggleButton></Tooltip>
              <Tooltip title="Data lineage"><ToggleButton value="data" data-testid="toggle-data"><CompareArrowsIcon fontSize="small" /></ToggleButton></Tooltip>
            </ToggleButtonGroup>

            <ToggleButtonGroup size="small" exclusive value={highlightMode} onChange={(e, v) => v && this.props.setHighlightMode(v)}>
              <Tooltip title="Full upstream/downstream"><ToggleButton value="full" data-testid="hl-full">Full</ToggleButton></Tooltip>
              <Tooltip title="Immediate only"><ToggleButton value="immediate" data-testid="hl-immediate">1-hop</ToggleButton></Tooltip>
            </ToggleButtonGroup>

            {/* Graph / Table display toggle */}
            <ToggleButtonGroup size="small" exclusive value={displayMode} onChange={(e, v) => v && this.props.setDisplayMode(v)}>
              <Tooltip title="Graph view"><ToggleButton value="graph" data-testid="toggle-display-graph"><GraphIcon fontSize="small" /></ToggleButton></Tooltip>
              <Tooltip title="Table view"><ToggleButton value="table" data-testid="toggle-display-table"><TableChartIcon fontSize="small" /></ToggleButton></Tooltip>
            </ToggleButtonGroup>

            {viewMode === 'edit' && (
              <>
                <Tooltip title="Group selection">
                  <span><Button size="small" disabled={selectedAppIds.length < 2} onClick={doGroup} data-testid="group-btn">Group</Button></span>
                </Tooltip>
                <Tooltip title="Ungroup">
                  <span><Button size="small" disabled={!selectedGroup} onClick={doUngroup} data-testid="ungroup-btn">Ungroup</Button></span>
                </Tooltip>
                {/* Tidy, Import and Print are graph-only — they call rfRef.fitView */}
                {displayMode === 'graph' && (
                  <>
                    {lineageViewType === 'system' && (
                      <Tooltip title="Auto-arrange layout">
                        <span><Button size="small" disabled={!map.nodes.length} onClick={() => this._doTidy()} data-testid="tidy-btn">Tidy</Button></span>
                      </Tooltip>
                    )}
                    {lineageViewType === 'data' && (
                      <Tooltip title="Re-arrange data view layout">
                        <span>
                          <Button size="small" disabled={!map.nodes.length} onClick={() => this._doTidyDataView()} data-testid="lineage-tidy-data-view">
                            Tidy
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title="Import lineage">
                      <span>
                        <Button
                          size="small"
                          startIcon={<ImportIcon />}
                          onClick={(e) => this.setState({importMenuAnchor: e.currentTarget})}
                          data-testid="toolbar-import-btn"
                        >
                          Import
                        </Button>
                      </span>
                    </Tooltip>
                    <Menu
                      anchorEl={this.state.importMenuAnchor}
                      open={Boolean(this.state.importMenuAnchor)}
                      onClose={() => this.setState({importMenuAnchor: null})}
                    >
                      <MenuItem
                        data-testid="import-menu-download"
                        onClick={() => {
                          this.setState({importMenuAnchor: null});
                          this.props.downloadTemplateFile();
                        }}
                      >
                        Download template
                      </MenuItem>
                      <MenuItem
                        data-testid="import-menu-upload"
                        onClick={() => this.setState({importMenuAnchor: null, showImportModal: true})}
                      >
                        Import from Excel…
                      </MenuItem>
                    </Menu>
                  </>
                )}
                <Tooltip title="Undo"><span><IconButton size="small" disabled={!canUndo} onClick={this.props.undo}><UndoIcon fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="Redo"><span><IconButton size="small" disabled={!canRedo} onClick={this.props.redo}><RedoIcon fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="Save (Cmd+S)">
                  <IconButton size="small" onClick={this.props.saveMap} data-testid="save-btn">
                    <SaveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Publish">
                  <span>
                    <Button
                      size="small" startIcon={<PublishIcon />}
                      onClick={() => this._doPublish()}
                      disabled={saveState === 'saving'}
                      data-testid="publish-btn"
                    >
                      Publish
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}

            <SaveStatus saveState={saveState} lastSavedAt={lastSavedAt} />

            {displayMode === 'graph' && (
              <Tooltip title="Print / Save as PDF">
                <IconButton size="small" onClick={() => this._doPrint()} data-testid="print-btn">
                  <PrintIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={viewMode === 'edit' ? 'Preview' : 'Edit'}>
              <Button size="small" onClick={() => this.props.setViewMode(viewMode === 'edit' ? 'view' : 'edit')} data-testid="mode-btn">
                {viewMode === 'edit' ? 'Preview' : 'Edit'}
              </Button>
            </Tooltip>
          </div>
        </div>

        {/* Body — canvas or table view */}
        {displayMode === 'graph' ? (
          <LineageCanvas />
        ) : (
          <LineageTableView />
        )}

        <NodeInspector />
        <ImportModal
          open={this.state.showImportModal}
          onClose={() => this.setState({showImportModal: false})}
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  map: state.lineageReducer.map,
  viewMode: state.lineageReducer.viewMode,
  lineageViewType: state.lineageReducer.lineageViewType,
  displayMode: state.lineageReducer.displayMode,
  highlightMode: state.lineageReducer.highlightMode,
  drawerOpen: state.lineageReducer.drawerOpen,
  historyIndex: state.lineageReducer.historyIndex,
  historyLength: state.lineageReducer.history.length,
  saveState: state.lineageReducer.saveState,
  lastSavedAt: state.lineageReducer.lastSavedAt,
  saveError: state.lineageReducer.saveError,
  charLoadError: state.lineageReducer.charLoadError,
  notification: state.lineageReducer.notification,
});

const mapDispatchToProps = {
  setMapProperty: A.setMapProperty,
  applyNodeChanges: A.applyNodeChanges,
  commitHistory: A.commitHistory,
  groupSelected: A.groupSelected,
  ungroup: A.ungroup,
  setLineageViewType: A.setLineageViewType,
  setHighlightMode: A.setHighlightMode,
  setViewMode: A.setViewMode,
  setDisplayMode: A.setDisplayMode,
  undo: A.undo,
  redo: A.redo,
  saveMap: A.saveMap,
  publishMap: A.publishMap,
  loadCharacteristics: A.loadCharacteristics,
  loadNodeRefs: A.loadNodeRefs,
  loadGlobalSettings: A.loadGlobalSettings,
  loadMap: A.loadMap,
  dismissNotify: A.dismissNotify,
  downloadTemplateFile: A.downloadTemplateFile,
  clearDataViewLayout: A.clearDataViewLayout,
};

export default connect(mapStateToProps, mapDispatchToProps)(LineageEditorShell);
